/**
 * PostHog Analytics — KVKK uyumlu, lazy-loaded kullanıcı davranış analizi.
 *
 * Strateji:
 *  - EU Cloud (Frankfurt) — veri AB'de kalır.
 *  - `posthog-js` modülü **dynamic import** ile yüklenir → eager bundle'da yok.
 *  - Anonim kullanıcı (consent yok) → PostHog **HİÇ** yüklenmez (~150 KB tasarruf).
 *  - Returning user (consent='granted') → bootstrap'ta init.
 *  - Yeni kullanıcı banner'da "Kabul Et" basınca → o anda yüklen + init.
 *  - opt_out_capturing_by_default: true → ek güvenlik katmanı.
 *  - Session replay default OFF.
 *
 * track(), identifyUser(), resetIdentity() — posthog yüklenmemişse no-op
 * (sessizce skip). Yüklü değilken çağrılan event'ler **kaybedilir**, bu
 * yüzden grantConsent() init'i tamamlamadan event çağrılmamalı.
 */
import type { AuthMe } from '@store/auth';
import type { default as PostHogModule } from 'posthog-js';

const CONSENT_KEY = 'zoprey_analytics_consent'; // 'granted' | 'denied'
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

// posthog-js instance — sadece dynamic import sonrası dolar
let posthogInstance: typeof PostHogModule | null = null;
let initializing: Promise<void> | null = null;

/**
 * PostHog'u dynamic import ile yükle ve init et.
 * Çağrılma noktaları:
 *  - main.tsx bootstrap: sadece consent='granted' ise (returning user)
 *  - CookieConsentBanner: kullanıcı "Kabul Et" basınca
 *
 * Idempotent — birden fazla çağrı tek init eder.
 */
export async function initPostHog(): Promise<void> {
  if (posthogInstance) return;
  if (initializing) return initializing;
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[posthog] VITE_POSTHOG_KEY yok, analytics devre dışı.');
    }
    return;
  }

  initializing = (async () => {
    const mod = await import('posthog-js');
    const posthog = mod.default;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // KVKK: kullanıcı onayı gelmeden hiçbir event gönderme (defansif çift kontrol)
      opt_out_capturing_by_default: true,
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: true,
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.debug(false);
      },
    });
    posthogInstance = posthog;
    // Önceden onay verilmişse opt-in otomatik aktif olsun.
    if (getStoredConsent() === 'granted') {
      posthog.opt_in_capturing();
    }
  })();

  await initializing;
}

export function getStoredConsent(): 'granted' | 'denied' | null {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(CONSENT_KEY);
  if (v === 'granted' || v === 'denied') return v;
  return null;
}

/** Kullanıcı banner'da "Kabul Et" → consent yaz + PostHog lazy yükle + opt-in. */
export async function grantConsent(): Promise<void> {
  localStorage.setItem(CONSENT_KEY, 'granted');
  // Henüz yüklenmemişse şimdi yükle (bu en kritik nokta — analytics burada başlar)
  await initPostHog();
  if (posthogInstance) {
    posthogInstance.opt_in_capturing();
  }
}

/** Kullanıcı banner'da "Sadece Zorunlu" → consent denied yaz. */
export function denyConsent(): void {
  localStorage.setItem(CONSENT_KEY, 'denied');
  // PostHog zaten yüklenmediği için opt_out etmemize gerek yok.
  if (posthogInstance) {
    posthogInstance.opt_out_capturing();
  }
}

/** Kullanıcı login sonrası — /auth/me cevabıyla çağır. */
export function identifyUser(me: AuthMe): void {
  if (!posthogInstance) return;
  if (posthogInstance.has_opted_out_capturing()) return;
  posthogInstance.identify(me.userId, {
    email: me.email,
    name: `${me.ad} ${me.soyad}`.trim(),
    tenant_id: me.tenantId,
    tenant_adi: me.tenantAdi,
    plan: me.plan,
  });
  posthogInstance.group('tenant', me.tenantId, {
    name: me.tenantAdi,
    plan: me.plan,
  });
}

/** Logout — kimliği temizle. */
export function resetIdentity(): void {
  if (!posthogInstance) return;
  posthogInstance.reset();
}

/**
 * Event tracking — type-safe sarmalayıcı.
 * posthog yüklenmemişse no-op (consent verilmemiş yeni kullanıcılar).
 */
export type AnalyticsEvent =
  // Auth
  | 'signup'
  | 'login'
  | 'logout'
  | 'password_reset_requested'
  // Müşteri
  | 'musteri_olusturuldu'
  | 'musteri_silindi'
  | 'kartvizit_tarandi'
  | 'ses_ile_musteri_tarandi'
  // Fırsat
  | 'firsat_olusturuldu'
  | 'firsat_kazanildi'
  | 'firsat_kaybedildi'
  // Teklif
  | 'teklif_olusturuldu'
  | 'teklif_gonderildi'
  | 'teklif_pdf_indirildi'
  | 'teklif_docx_indirildi'
  | 'teklif_eposta_gonderildi';

export function track(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
): void {
  if (!posthogInstance) return;
  if (posthogInstance.has_opted_out_capturing()) return;
  posthogInstance.capture(event, properties);
}
